/**
 * ImpersonationBanner - faixa fixa da aba de suporte ("entrar como usuário").
 *
 * Mostra quem está sendo impersonado, o tempo restante e o botão Sair.
 * Ao vencer, encerra sozinha. Também marca o título da aba e o Sentry,
 * para erros reproduzidos aqui serem fáceis de achar.
 */

import React, { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { LogOut, ShieldAlert } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { formatRemaining, isImpersonationExpired } from '@/lib/impersonation';

const TITLE_PREFIX = '[SUPORTE] ';

const ImpersonationBanner: React.FC = () => {
  const { impersonation, endImpersonation } = useAuth();
  const [now, setNow] = useState(() => Date.now());
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (!impersonation) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [impersonation]);

  useEffect(() => {
    if (!impersonation) return;
    Sentry.setTag('impersonation', 'true');
    Sentry.setTag('impersonation_session', impersonation.sessionId);
    Sentry.setUser({ id: impersonation.target.id });
    const syncTitle = () => {
      if (!document.title.startsWith(TITLE_PREFIX)) document.title = TITLE_PREFIX + document.title;
    };
    syncTitle();
    const titleTimer = setInterval(syncTitle, 2000);
    return () => clearInterval(titleTimer);
  }, [impersonation]);

  useEffect(() => {
    if (impersonation && isImpersonationExpired(impersonation, now)) endImpersonation();
  }, [impersonation, now, endImpersonation]);

  if (!impersonation) return null;

  const name = impersonation.target.displayName || impersonation.target.email || impersonation.target.id;
  const handleExit = async () => {
    setLeaving(true);
    await endImpersonation();
  };

  return (
    <div
      role="status"
      className="fixed bottom-0 inset-x-0 z-[100] flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-amber-500 px-4 py-2 text-sm text-black shadow-lg"
    >
      <ShieldAlert className="h-4 w-4 shrink-0" />
      <span className="min-w-0 break-words text-center">
        Você está como <strong>{name}</strong>
        {impersonation.target.email && impersonation.target.displayName ? ` (${impersonation.target.email})` : ''}
        {' '}— sessão de suporte, expira em <span className="font-mono">{formatRemaining(impersonation.expiresAt, now)}</span>
      </span>
      <button
        onClick={handleExit}
        disabled={leaving}
        className="inline-flex items-center gap-1 rounded-md bg-black/85 px-3 py-1 font-medium text-white hover:bg-black disabled:opacity-60"
      >
        <LogOut className="h-4 w-4" /> {leaving ? 'Saindo…' : 'Sair'}
      </button>
    </div>
  );
};

export default ImpersonationBanner;
